import { useState, useEffect, useCallback } from 'react';
import { T } from '../components/ui';
import { analyticsApi, type SupplierStat, type AlertPattern } from '../api/client';

// ── Tiny sparkline component (SVG, no chart library needed) ───────────────────
function Sparkline({ data, width = 200, height = 48, color = T.copper }: {
 data: { date: string; score: number }[];
 width?: number; height?: number; color?: string;
}) {
 if (data.length < 2) return <span style={{ color: T.dim, fontSize: 12 }}>No data</span>;
 const scores = data.map(d => d.score);
 const min = Math.min(...scores);
 const max = Math.max(...scores);
 const range = max - min || 1;
 const pts = data.map((d, i) => {
 const x = (i / (data.length - 1)) * width;
 const y = height - ((d.score - min) / range) * (height - 8) - 4;
 return `${x},${y}`;
 }).join(' ');
 const lastScore = scores[scores.length - 1];
 const firstScore = scores[0];
 const delta = lastScore - firstScore;
 return (
 <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 <svg width={width} height={height} style={{ overflow: 'visible' }}>
 <polyline
 points={pts}
 fill="none"
 stroke={color}
 strokeWidth="2"
 strokeLinecap="round"
 strokeLinejoin="round"
 opacity={0.8}
 />
 {/* Last point dot */}
 {(() => {
 const last = pts.split(' ').pop()!.split(',');
 return <circle cx={last[0]} cy={last[1]} r="3" fill={color} />;
 })()}
 </svg>
 <span style={{
 fontSize: 11, fontWeight: 600,
 color: delta > 0 ? '#7eb89a' : delta < 0 ? '#c0454a' : '#8a8178'
 }}>
 {delta > 0 ? '+' : ''}{delta.toFixed(0)}
 </span>
 </div>
 );
}

// ── Score dial (mini, inline) ──────────────────────────────────────────────────
function MiniDial({ score, size = 40 }: { score: number; size?: number }) {
 const r = (size / 2) - 4;
 const circ = 2 * Math.PI * r;
 const offset = circ * (1 - score / 100);
 const color = score >= 85 ? '#7eb89a' : score >= 70 ? '#5b9bd5' : score >= 50 ? '#d4a847' : '#c0454a';
 return (
 <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
 <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#2a2620" strokeWidth="3" />
 <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="3"
 strokeDasharray={circ} strokeDashoffset={offset}
 strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`} />
 <text x={size/2} y={size/2 + 4} textAnchor="middle" fill={color}
 fontSize={size < 40 ? 9 : 11} fontWeight="700" fontFamily="-apple-system,sans-serif">
 {score}
 </text>
 </svg>
 );
}

// ── Risk badge ────────────────────────────────────────────────────────────────
function RiskBadge({ level }: { level: string }) {
 const colors: Record<string, { bg: string; color: string }> = {
 LOW: { bg: 'rgba(126,184,154,0.15)', color: T.sage },
 MEDIUM: { bg: 'rgba(212,168,71,0.15)', color: T.amber },
 HIGH: { bg: 'rgba(232,147,90,0.15)', color: T.copper },
 };
 const c = colors[level?.toUpperCase()] || colors.MEDIUM;
 return (
 <span style={{
 fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
 background: c.bg, color: c.color
 }}>{level || 'MEDIUM'}</span>
 );
}

const STYLES = {
 page: { padding: '24px', maxWidth: 1200 },
 heading: { fontSize: 24, fontWeight: 800, color: T.text, marginBottom: 4 },
 sub: { fontSize: 13, color: T.sub, marginBottom: 28 },
 tabs: { display: 'flex', gap: 4, marginBottom: 24, background: T.panel, borderRadius: 10, padding: 4, width: 'fit-content' as const },
 tab: (active: boolean): React.CSSProperties => ({
 padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
 fontSize: 13, fontWeight: 600,
 background: active ? '#e8935a' : 'transparent',
 color: active ? '#fff' : '#8a8178',
 transition: 'all 0.12s',
 }),
 card: { background: T.panel, border: `1px solid ${T.border}`, borderRadius: 14, padding: 20, marginBottom: 16 },
 label: { fontSize: 11, color: T.sub, textTransform: 'uppercase' as const, letterSpacing: '0.5px', marginBottom: 6 },
 row: { display: 'flex', gap: 16, marginBottom: 16 },
 statBox: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: '14px 18px', flex: 1, minWidth: 0 },
 statNum: { fontSize: 28, fontWeight: 800, color: T.copper },
 statLbl: { fontSize: 11, color: T.sub, marginTop: 2 },
 btn: (variant: 'primary' | 'secondary' | 'ghost' = 'secondary'): React.CSSProperties => ({
 padding: variant === 'ghost' ? '6px 14px' : '10px 20px',
 borderRadius: 8, border: variant === 'secondary' ? '1px solid #2a2620' : 'none',
 background: variant === 'primary' ? '#e8935a' : variant === 'ghost' ? 'transparent' : '#141310',
 color: variant === 'primary' ? '#fff' : '#f0ebe4',
 fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' as const,
 }),
 table: { width: '100%', borderCollapse: 'collapse' as const },
 th: { textAlign: 'left' as const, padding: '10px 12px', fontSize: 11, color: T.sub, textTransform: 'uppercase' as const, borderBottom: '1px solid #2a2620' },
 td: { padding: '12px', fontSize: 13, color: T.text, borderBottom: '1px solid #1a1815' },
 pill: (color: string): React.CSSProperties => ({
 display: 'inline-block', padding: '2px 10px', borderRadius: 20,
 background: color + '20', color, fontSize: 10, fontWeight: 700,
 }),
 empty: { textAlign: 'center' as const, padding: '40px 0', color: T.dim, fontSize: 13 },
};

// ═══════════════════════════════════════════════════════════════════════════
// Tab 1: Score Trends
// ═══════════════════════════════════════════════════════════════════════════
function ScoreTrendsTab() {
 const [productId, setProductId] = useState('');
 const [days, setDays] = useState<30 | 90 | 180>(90);
 const [trendData, setTrendData] = useState<any>(null);
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState('');

 const fetchTrend = async () => {
 if (!productId.trim()) return;
 setLoading(true); setError('');
 try {
 const data = await analyticsApi.getScoreTrend(productId.trim(), days);
 setTrendData(data);
 } catch (e: any) {
 setError(e.message || 'Failed to fetch trend data');
 } finally { setLoading(false); }
 };

 return (
 <div>
 <div style={{ ...STYLES.card, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
 <div style={{ flex: 1, minWidth: 240 }}>
 <div style={STYLES.label}>Product ID</div>
 <input
 value={productId}
 onChange={e => setProductId(e.target.value)}
 onKeyDown={e => e.key === 'Enter' && fetchTrend()}
 placeholder="Paste a product UUID..."
 style={{
 width: '100%', padding: '10px 14px', background: T.surface,
 border: `1px solid ${T.border}`, borderRadius: 8, color: T.text,
 fontSize: 13, outline: 'none',
 }}
 />
 </div>
 <div>
 <div style={STYLES.label}>Range</div>
 <div style={{ display: 'flex', gap: 4 }}>
 {([30, 90, 180] as const).map(d => (
 <button key={d} onClick={() => setDays(d)} style={{
 ...STYLES.btn(days === d ? 'primary' : 'secondary'),
 padding: '10px 14px',
 }}>{d}d</button>
 ))}
 </div>
 </div>
 <button onClick={fetchTrend} disabled={loading || !productId.trim()} style={STYLES.btn('primary')}>
 {loading ? 'Loading…' : 'Load Trend'}
 </button>
 </div>

 {error && <div style={{ color: '#c0454a', fontSize: 13, marginBottom: 12 }}>{error}</div>}

 {trendData && (
 <>
 {/* Product header */}
 <div style={STYLES.card}>
 <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
 <MiniDial score={trendData.product.provenance_score} size={56} />
 <div>
 <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{trendData.product.name}</div>
 <div style={{ fontSize: 13, color: T.sub }}>{trendData.product.brand || ''}</div>
 </div>
 <div style={{ marginLeft: 'auto', display: 'flex', gap: 16 }}>
 <div style={STYLES.statBox}>
 <div style={{ ...STYLES.statNum, fontSize: 20 }}>{trendData.product.provenance_score}</div>
 <div style={STYLES.statLbl}>Current Score</div>
 </div>
 <div style={STYLES.statBox}>
 <div style={{
 ...STYLES.statNum, fontSize: 20,
 color: (trendData.change_30d ?? 0) > 0 ? '#7eb89a' : (trendData.change_30d ?? 0) < 0 ? '#c0454a' : '#8a8178'
 }}>
 {trendData.change_30d != null
 ? ((trendData.change_30d > 0 ? '+' : '') + trendData.change_30d)
 : '—'}
 </div>
 <div style={STYLES.statLbl}>30-day change</div>
 </div>
 </div>
 </div>
 </div>

 {/* Sparkline chart */}
 <div style={STYLES.card}>
 <div style={{ ...STYLES.label, marginBottom: 12 }}>
 Score Over {days} Days ({trendData.trend.length} data points)
 </div>
 {trendData.trend.length < 2 ? (
 <div style={STYLES.empty}>Not enough historical data for this range.</div>
 ) : (
 <div style={{ overflowX: 'auto' }}>
 <Sparkline
 data={trendData.trend}
 width={Math.max(400, trendData.trend.length * 12)}
 height={72}
 color="#e8935a"
 />
 </div>
 )}
 </div>

 {/* Event log */}
 {trendData.trend.length > 0 && (
 <div style={STYLES.card}>
 <div style={STYLES.label}>Score History</div>
 <table style={STYLES.table}>
 <thead>
 <tr>
 <th style={STYLES.th}>Date</th>
 <th style={STYLES.th}>Score</th>
 <th style={STYLES.th}>Change</th>
 <th style={STYLES.th}>Trigger</th>
 </tr>
 </thead>
 <tbody>
 {[...trendData.trend].reverse().slice(0, 30).map((row: any, i: number) => (
 <tr key={i}>
 <td style={STYLES.td}>{row.date}</td>
 <td style={STYLES.td}><strong>{row.score}</strong></td>
 <td style={STYLES.td}>
 <span style={{ color: row.delta > 0 ? '#7eb89a' : row.delta < 0 ? '#c0454a' : '#8a8178', fontWeight: 600 }}>
 {row.delta > 0 ? '+' : ''}{row.delta}
 </span>
 </td>
 <td style={STYLES.td}>
 <span style={STYLES.pill('#8a8178')}>{row.trigger_type || 'manual'}</span>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </>
 )}
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════════════════
// Tab 2: Supplier League Table
// ═══════════════════════════════════════════════════════════════════════════
function SupplierLeagueTab() {
 const [suppliers, setSuppliers] = useState<SupplierStat[]>([]);
 const [loading, setLoading] = useState(true);
 const [riskFilter, setRiskFilter] = useState('');
 const [sortBy, setSortBy] = useState<string>('rank');
 const [refreshedAt, setRefreshedAt] = useState<string | null>(null);

 const load = useCallback(async () => {
 setLoading(true);
 try {
 const data = await analyticsApi.getSupplierLeague({
 risk_tier: riskFilter || undefined,
 sort_by: sortBy || undefined,
 });
 setSuppliers(data.suppliers);
 setRefreshedAt(data.refreshed_at);
 } finally { setLoading(false); }
 }, [riskFilter, sortBy]);

 useEffect(() => { load(); }, [load]);

 const SORT_OPTIONS = [
 { value: 'rank', label: '🏆 Overall Rank' },
 { value: 'avg_score', label: '📈 Score' },
 { value: 'active_alert_count',label: '⚠ Active Alerts' },
 { value: 'recall_count', label: '🚨 Recalls' },
 { value: 'cert_coverage_pct', label: '📋 Certifications' },
 ];

 return (
 <div>
 {/* Controls */}
 <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
 <select
 value={riskFilter}
 onChange={e => setRiskFilter(e.target.value)}
 style={{
 padding: '9px 14px', background: T.panel, border: `1px solid ${T.border}`,
 borderRadius: 8, color: T.text, fontSize: 13, cursor: 'pointer',
 }}
 >
 <option value="">All Risk Tiers</option>
 <option value="LOW">Low Risk</option>
 <option value="MEDIUM">Medium Risk</option>
 <option value="HIGH">High Risk</option>
 </select>
 <select
 value={sortBy}
 onChange={e => setSortBy(e.target.value)}
 style={{
 padding: '9px 14px', background: T.panel, border: `1px solid ${T.border}`,
 borderRadius: 8, color: T.text, fontSize: 13, cursor: 'pointer',
 }}
 >
 {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
 </select>
 <button onClick={load} style={STYLES.btn('ghost')}>↻ Refresh</button>
 {refreshedAt && (
 <span style={{ fontSize: 11, color: T.dim, marginLeft: 4 }}>
 Updated {new Date(refreshedAt).toLocaleTimeString()}
 </span>
 )}
 </div>

 <div style={STYLES.card}>
 {loading ? (
 <div style={STYLES.empty}>Loading league table…</div>
 ) : suppliers.length === 0 ? (
 <div style={STYLES.empty}>No suppliers found for this filter.</div>
 ) : (
 <table style={STYLES.table}>
 <thead>
 <tr>
 <th style={STYLES.th}>#</th>
 <th style={STYLES.th}>Supplier</th>
 <th style={STYLES.th}>Country</th>
 <th style={STYLES.th}>Score</th>
 <th style={STYLES.th}>Products</th>
 <th style={STYLES.th}>Active Alerts</th>
 <th style={STYLES.th}>Recalls</th>
 <th style={STYLES.th}>Cert Cover</th>
 <th style={STYLES.th}>Risk</th>
 </tr>
 </thead>
 <tbody>
 {suppliers.map(s => (
 <tr key={s.supplier_id}
 style={{ transition: 'background 0.1s' }}
 onMouseEnter={e => (e.currentTarget.style.background = '#1a1815')}
 onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
 >
 <td style={{ ...STYLES.td, fontWeight: 700, color: T.sub }}>
 {s.rank <= 3 ? ['🥇', '🥈', '🥉'][s.rank - 1] : s.rank}
 </td>
 <td style={STYLES.td}>
 <div style={{ fontWeight: 600 }}>{s.name}</div>
 {s.verified && <div style={{ fontSize: 10, color: T.sage, marginTop: 2 }}>✓ Verified</div>}
 </td>
 <td style={{ ...STYLES.td, color: T.sub }}>{s.country || '—'}</td>
 <td style={STYLES.td}><MiniDial score={Math.round(s.avg_score || 0)} size={36} /></td>
 <td style={STYLES.td}>{s.product_count}</td>
 <td style={STYLES.td}>
 <span style={s.active_alert_count > 0 ? STYLES.pill('#c0454a') : { color: T.dim }}>
 {s.active_alert_count}
 </span>
 </td>
 <td style={STYLES.td}>
 <span style={s.recall_count > 0 ? STYLES.pill('#c0454a') : { color: T.dim }}>
 {s.recall_count}
 </span>
 </td>
 <td style={STYLES.td}>
 <div style={{
 height: 6, background: '#2a2620', borderRadius: 3, width: 80, overflow: 'hidden'
 }}>
 <div style={{
 height: '100%', borderRadius: 3, width: `${s.cert_coverage_pct || 0}%`,
 background: (s.cert_coverage_pct || 0) >= 80 ? '#7eb89a' : '#d4a847',
 transition: 'width 0.6s',
 }} />
 </div>
 <div style={{ fontSize: 10, color: T.sub, marginTop: 2 }}>
 {s.cert_coverage_pct?.toFixed(0) || 0}%
 </div>
 </td>
 <td style={STYLES.td}><RiskBadge level={s.risk_level} /></td>
 </tr>
 ))}
 </tbody>
 </table>
 )}
 </div>

 {/* Insight callout */}
 {suppliers.length > 0 && (() => {
 const highRisk = suppliers.filter(s => s.risk_level === 'HIGH');
 const topRisk = highRisk.slice(0, 2).map(s => s.name).join(', ');
 const risky = suppliers.filter(s => s.active_alert_count > 0);
 return highRisk.length > 0 ? (
 <div style={{ ...STYLES.card, background: '#1a1008', borderColor: '#c07840' }}>
 <div style={{ fontSize: 12, color: '#c07840', fontWeight: 700, marginBottom: 6 }}>
 💡 Risk Insight
 </div>
 <div style={{ fontSize: 13, color: '#e0c8a0', lineHeight: 1.5 }}>
 {highRisk.length} supplier{highRisk.length !== 1 ? 's' : ''} rated HIGH risk
 {topRisk ? ` (${topRisk}${highRisk.length > 2 ? '…' : ''})` : ''}.
 {' '}{risky.length > 0 && `${risky.length} supplier${risky.length !== 1 ? 's' : ''} have open alerts.`}
 </div>
 </div>
 ) : null;
 })()}
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════════════════
// Tab 3: Alert Patterns
// ═══════════════════════════════════════════════════════════════════════════
function AlertPatternsTab() {
 const [pattern, setPattern] = useState<(AlertPattern & { available: boolean }) | null>(null);
 const [loading, setLoading] = useState(true);
 const [generating, setGenerating] = useState(false);

 const load = async () => {
 setLoading(true);
 try { setPattern(await analyticsApi.getAlertPattern()); }
 finally { setLoading(false); }
 };

 const generate = async () => {
 setGenerating(true);
 try {
 await analyticsApi.generateAlertPattern();
 // Poll for completion
 let tries = 0;
 const poll = setInterval(async () => {
 tries++;
 const p = await analyticsApi.getAlertPattern();
 setPattern(p);
 if (p.available || tries > 15) clearInterval(poll);
 }, 2000);
 } catch (e: any) {
 alert(e.message || 'Generation failed');
 } finally { setGenerating(false); }
 };

 useEffect(() => { load(); }, []);

 if (loading) return <div style={STYLES.empty}>Loading pattern analysis…</div>;

 if (!pattern?.available) {
 return (
 <div style={STYLES.card}>
 <div style={{ textAlign: 'center', padding: '40px 0' }}>
 <div style={{ fontSize: 36, marginBottom: 12 }}>📊</div>
 <div style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 8 }}>
 No alert pattern analysis yet
 </div>
 <div style={{ fontSize: 13, color: T.sub, marginBottom: 20 }}>
 Generate a weekly digest to see AI-powered pattern analysis of your alert data.
 </div>
 <button onClick={generate} disabled={generating} style={STYLES.btn('primary')}>
 {generating ? '⏳ Generating…' : '✨ Generate Weekly Digest'}
 </button>
 </div>
 </div>
 );
 }

 return (
 <div>
 {/* Header */}
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
 <div>
 <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>
 Week of {pattern.week_start} — {pattern.week_end}
 </div>
 {pattern.ai_generated && pattern.generated_at && (
 <div style={{ fontSize: 11, color: T.dim, marginTop: 2 }}>
 AI analysis generated {new Date(pattern.generated_at).toLocaleString()}
 </div>
 )}
 </div>
 <button onClick={generate} disabled={generating} style={STYLES.btn('secondary')}>
 {generating ? '⏳ Regenerating…' : '↻ Refresh'}
 </button>
 </div>

 {/* Stats row */}
 <div style={STYLES.row}>
 {[
 { num: pattern.total_alerts, lbl: 'Total Alerts', color: T.text },
 { num: pattern.critical_count, lbl: 'Critical', color: '#c0454a' },
 { num: pattern.high_count, lbl: 'High Severity', color: T.copper },
 { num: pattern.resolved_pct != null ? `${pattern.resolved_pct}%` : '—', lbl: 'Resolved Rate', color: T.sage },
 ].map(({ num, lbl, color }) => (
 <div key={lbl} style={STYLES.statBox}>
 <div style={{ ...STYLES.statNum, color }}>{num}</div>
 <div style={STYLES.statLbl}>{lbl}</div>
 </div>
 ))}
 </div>

 {/* AI Narrative */}
 {pattern.narrative && (
 <div style={{ ...STYLES.card, background: '#0a1520', borderColor: '#1a3a5c' }}>
 <div style={{ ...STYLES.label, color: '#5b9bd5', marginBottom: 8 }}>
 {pattern.ai_generated ? '✨ AI Analysis' : '📊 Summary'}
 </div>
 <p style={{ fontSize: 13, color: '#c8d8e8', lineHeight: 1.6, margin: 0 }}>
 {pattern.narrative}
 </p>
 </div>
 )}

 {/* Recommendations */}
 {pattern.recommendations?.length > 0 && (
 <div style={STYLES.card}>
 <div style={STYLES.label}>Recommended Actions</div>
 {pattern.recommendations.map((r, i) => (
 <div key={i} style={{
 display: 'flex', gap: 10, alignItems: 'flex-start',
 padding: '10px 0', borderBottom: i < pattern.recommendations.length - 1 ? '1px solid #1a1815' : 'none'
 }}>
 <span style={{
 ...STYLES.pill(r.priority === 'high' ? '#c0454a' : r.priority === 'low' ? '#4a4540' : '#d4a847'),
 flexShrink: 0, marginTop: 1
 }}>
 {r.priority}
 </span>
 <span style={{ fontSize: 13, color: T.text, lineHeight: 1.5 }}>{r.text}</span>
 </div>
 ))}
 </div>
 )}

 {/* Top suppliers */}
 {pattern.top_suppliers?.length > 0 && (
 <div style={STYLES.card}>
 <div style={STYLES.label}>Top Suppliers by Alert Count</div>
 {pattern.top_suppliers.map((s, i) => (
 <div key={i} style={{
 display: 'flex', justifyContent: 'space-between', alignItems: 'center',
 padding: '10px 0',
 borderBottom: i < pattern.top_suppliers.length - 1 ? '1px solid #1a1815' : 'none',
 }}>
 <div>
 <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{s.supplier_name}</div>
 {s.top_type && <div style={{ fontSize: 11, color: T.sub, marginTop: 2 }}>
 Most common: {s.top_type.replace(/_/g, ' ')}
 </div>}
 </div>
 <span style={STYLES.pill(s.alert_count > 5 ? '#c0454a' : '#d4a847')}>
 {s.alert_count} alert{s.alert_count !== 1 ? 's' : ''}
 </span>
 </div>
 ))}
 </div>
 )}

 {/* Top alert types */}
 {pattern.top_alert_types?.length > 0 && (
 <div style={STYLES.card}>
 <div style={STYLES.label}>Alert Type Breakdown</div>
 {pattern.top_alert_types.map((t, i) => {
 const pct = pattern.total_alerts > 0 ? Math.round((t.count / pattern.total_alerts) * 100) : 0;
 return (
 <div key={i} style={{ marginBottom: 10 }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
 <span style={{ fontSize: 13, color: T.text }}>{t.type.replace(/_/g, ' ')}</span>
 <span style={{ fontSize: 12, color: T.sub }}>{t.count} ({pct}%)</span>
 </div>
 <div style={{ height: 4, background: '#2a2620', borderRadius: 2 }}>
 <div style={{ height: '100%', width: `${pct}%`, background: T.copper, borderRadius: 2, transition: 'width 0.6s' }} />
 </div>
 </div>
 );
 })}
 </div>
 )}
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════════════════
// Main Page
// ═══════════════════════════════════════════════════════════════════════════
export default function AnalyticsPage() {
 const [activeTab, setActiveTab] = useState<'trends' | 'league' | 'patterns'>('trends');

 return (
 <div style={STYLES.page}>
 <div style={STYLES.heading}>Analytics</div>
 <div style={STYLES.sub}>
 Score trend charts, supplier performance league, and AI alert pattern analysis
 </div>

 <div style={STYLES.tabs}>
 {[
 { id: 'trends' as const, label: '📈 Score Trends' },
 { id: 'league' as const, label: '🏆 Supplier League' },
 { id: 'patterns' as const, label: '🔍 Alert Patterns' },
 ].map(tab => (
 <button key={tab.id} style={STYLES.tab(activeTab === tab.id)} onClick={() => setActiveTab(tab.id)}>
 {tab.label}
 </button>
 ))}
 </div>

 {activeTab === 'trends' && <ScoreTrendsTab />}
 {activeTab === 'league' && <SupplierLeagueTab />}
 {activeTab === 'patterns' && <AlertPatternsTab />}
 </div>
 );
}
